import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  AbilityEffect,
  Condition,
  Potence,
  CharacterReceivable,
  CharacterReceivableItem,
  CharacterCondition,
  DamageMultiplierSimple
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import { DisablableOption } from '@models';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { DamageType, isDamageType } from '@dndModels/Types';

interface Props {
  character: Character;
  effect: AbilityEffect;
  potence: Potence;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

const multiplier_map = {
  "Resistance": 0.5,
  "Vulnerability": 2,
  "Immunity": 0
}

export default function CharacterSelfConditionEffect({
  character: char,
  effect,
  potence,
  disabled = false,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [options, setOptions] = useState<DisablableOption[]>([]);
  // const [ping, setPing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [selected_id, setSelectedId] = useState<string>("");
  const [condition_hash, setConditionHash] = useState<{ [id: string]: { level: number, source_true_id: string } }>({});
  const [open, setOpen] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        // margin: "4px",
        backgroundColor: theme.palette.secondary.main
      },
    });
  });   

  const copyHash = useCallback(() => {
    const new_hash: { [id: string]: { level: number, source_true_id: string } } = {};
    for (const char_cond of char.conditions) {
      new_hash[char_cond.source_id] = { 
        level: char_cond.level, 
        source_true_id: char_cond.condition_source_true_id 
      };
    }
    // for (const key of Object.keys(character.condition_hash)) {
    //   new_hash[key] = {...character.condition_hash[key]};
    // }
    setConditionHash(new_hash);
  }, [char]);

  const load = useCallback( async () => {
    setLoading(true);
    setFetched(true);
    const res = await api.getSetOfObjectsWithCallbacks({ 
      data_types: ["condition"],
    });
    if (res && !res.error) {
      const new_conditions = res.condition as Condition[];
      setConditions(new_conditions);
      
      const self_condition_ids = 
        // alternate_condition_features.length === 0 ? 
        potence.conditions_applied; // : potence.conditions_applied.map(o => {
      //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
      //   if (alternate_condition_feature) {
      //     return alternate_condition_feature.alternate_condition_id;
      //   } else {
      //     return o;
      //   }
      // });

      // console.log(self_condition_ids);
      const new_options = self_condition_ids.map(id => {
        const opt: DisablableOption = {
          id,
          text: id
        };
        const cond = new_conditions.find(o => o._id === id);
        if (cond) {
          opt.text = cond.name;
        } 
        return opt;
      });
      setOptions(new_options);
      setLoading(false);
      copyHash();
    }
  }, [copyHash]);
   
  useEffect(() => {
    if (!fetched) {
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (changing) {
      // console.log("changing");
      setChanging(false);
      setProcessing(true);
      setTimeout(() => {
        char.changed();
        char.subscription_holder.recalculate();
      }, 1000);
    }
    // eslint-disable-next-line
  }, [changing]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));

  const { classes, cx } = useStyles();
  
  const renderCharacters = ({ condition, damage_type, multiplier_str, level }: 
    { condition?: Condition, damage_type?: DamageType, multiplier_str?: string, level: number }) => {
    const campaign = char.campaign;
    if (campaign) {
      return campaign.characters.filter(o => o.active && !o.is_archived && o._id !== char._id).map(char => {
        return (
          <Grid key={char._id} size={12}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid size={9}>
                  { char.name }
                </Grid>
                <Grid size={3}>
                  <DandDButtonBox 
                    name="Apply"
                    disabled={processing}
                    onClick={() => {
                      const receivable = new CharacterReceivable();
                      receivable.from_id = char._id;
                      receivable.char_id = char._id;
                      receivable.sent_dt = common_util.get_time_number();
                      if (condition) {
                        receivable.items = [new CharacterReceivableItem({
                          type: "Condition",
                          obj_id: condition._id,
                          count: level,
                        })];
                      } else if (damage_type && multiplier_str) {
                        receivable.items = [new CharacterReceivableItem({
                          type: "Condition",
                          obj_id: `${damage_type} ${multiplier_str}`,
                          count: level,
                        })];
                      }
                      setProcessing(true);
                      api.upsertObject("character_receivable", receivable).then(res => {
                        setProcessing(false);
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }

  const renderMinions = ({ condition, damage_type, multiplier_str, level }: 
    { condition?: Condition, damage_type?: DamageType, multiplier_str?: string, level: number }) => {
    if (char instanceof Character) {
      return char.minions.map(minion => {
        return (
          <Grid key={minion.true_id} size={12}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid size={9}>
                  { minion.name }
                </Grid>
                <Grid size={3}>
                  <DandDButtonBox 
                    name="Apply"
                    disabled={processing}
                    onClick={ async () => {
                      if (condition) {
                        let cc = minion.conditions.find(o => o.source_id === condition._id);
                        if (!cc) {
                          await api.loadObject(condition);
                          cc = new CharacterCondition();
                          await cc.copy_source({ char: char, feature_source: condition });
                          cc.level = 1;
                          minion.conditions.push(cc);
                        }
                        cc.level = level;
                      } else if (damage_type && multiplier_str) {
                        console.error("Not Implemented");
                        // let cc = minion..find(o => o.source_id === condition._id);
                        // if (!cc) {
                        //   await api.loadObject(condition);
                        //   cc = new CharacterCondition();
                        //   cc.copy_source({ char: character, feature_source: condition });
                        //   cc.level = 1;
                        //   minion.conditions.push(cc);
                        // }
                      }
                      char.subscription_holder.recalculate();
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }
  
  const renderExtras = () => {
    let condition: Condition | undefined = undefined;
    let damage_type: DamageType = "Typeless";
    let multiplier = -1;
    let multiplier_str = "";
    let level = 1;
    if (options.length === 1) {
      const id = options[0].id;
      condition = conditions.find(o => o._id === id);
      if (condition) {
        if (condition.max_level > 1) {
          level = char_util.value(potence.condition_levels_applied, { obj: char });
        } 
        if (potence.remove_condition_levels) {
          level = -1 * level;
        }
      } else {
        const pieces = id.split(" ");
        if (pieces.length === 2) {
          const dt = pieces[0];
          multiplier_str = pieces[1];
          if ((multiplier_str === "Resistance" || 
            multiplier_str === "Vulnerability" || 
            multiplier_str === "Immunity") && isDamageType(dt)) {
            multiplier = multiplier_map[multiplier_str];
            damage_type = dt;
            level = char_util.value(potence.condition_levels_applied, { obj: char });
            if (potence.remove_condition_levels) {
              level = -1 * level;
            }
          }
        }
        if (multiplier === -1 || damage_type === "Typeless") {
          return <>Invalid Condition</>;
        }
      }
    } else {
      condition = conditions.find(o => o._id === selected_id);
    }
    if (condition && level !== 0) {
      return (
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}>
          { open &&
            <Grid container spacing={0} direction="row" 
              style={{ maxHeight: window.innerHeight - 34 }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                { level === 1 ? 
                  `Add ${condition.name} to Allies`
                : level === -1 ? 
                  `Remove ${condition.name} from Allies`
                : level < 0 ? 
                  `Remove ${level * -1} levels of ${condition.name} from Allies`
                :
                  `Add ${level} levels of ${condition.name} to Allies`
                }
              </Grid>
              { renderCharacters({condition, level}) }
              { renderMinions({condition, level}) }
            </Grid>
          }
        </ModalOrDrawer>
      );
    } else if (multiplier !== -1 && damage_type !== "Typeless") {
      return (
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}>
          { open &&
            <Grid container spacing={0} direction="row" 
              style={{ maxHeight: window.innerHeight - 34 }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                { level === 1 ? 
                  `Add ${damage_type} ${multiplier_str} to Allies`
                : level === -1 && 
                  `Remove ${damage_type} ${multiplier_str} from Allies`
                }
              </Grid>
              { renderCharacters({ damage_type, multiplier_str, level}) }
              { renderMinions({ damage_type, multiplier_str, level}) }
            </Grid>
          }
        </ModalOrDrawer>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    // const alternate_condition_features = char.alternate_condition_features.filter(o => potence.conditions_applied.includes(o.original_condition_id));

    const renderApplyRemove = () => {
      return (
        <Grid container spacing={0} direction="row">
          { renderSelected() }
          { renderRemoveAny() }
        </Grid>
      );
    }
    
    const renderSelected = () => {
      if (selected_id === "") {
        return (
          <Grid size={12}>
            <DandDButtonBox
              name="First Select a Condition"
              style={{
                fontSize: "9px",
              }}
              disabled
              onClick={() => {}} 
            />
          </Grid>
        );
      } else {
        const cond = conditions.find(o => o._id === selected_id);
        if (cond) {
          const has_condition = char_util.hasCondition(char, [selected_id], 1);
          return (
            <Grid size={12}>
              <DandDButtonBox
                name={ has_condition ? 
                  "Remove" : 
                  "Apply"
                }
                style={{
                  fontSize: "9px",
                }}
                disabled={ !enabled || processing }
                onClick={ async () => {
                  debugger;
                  const cc = char.conditions.find(o => o.source_id === selected_id);
                  if (cc) {
                    char_util.removeCharFeatureBasesFor(char, cc);
                    char.conditions = char.conditions.filter(o => o.source_id !== cc.source_id);
                  } else {
                    const obj = data_util.conditions.find(o => o._id === selected_id);
                    if (obj) {
                      await api.loadObject(obj);
                      const cc = new CharacterCondition();
                      await cc.copy_source({ char: char, feature_source: obj });
                      cc.level = 1;
                      char.conditions.push(cc);
                    }
                  }
                  copyHash();
                  char.changed();
                  char.subscription_holder.recalculate();
                }} 
              />
            </Grid>
          );
        } else {
          const pieces = selected_id.split(" ");
          let multiplier = -1;
          let multiplier_str = "";
          let damage_type: DamageType = "Typeless";
          if (pieces.length === 2) {
            const dt = pieces[0];
            multiplier_str = pieces[1];
            if ((multiplier_str === "Resistance" || 
              multiplier_str === "Vulnerability" || 
              multiplier_str === "Immunity") && isDamageType(dt)) {
              multiplier = multiplier_map[multiplier_str];
              damage_type = dt;
              const has_condition = char.extra_damage_multipliers.some(o => 
                o.damage_type === damage_type && o.multiplier === multiplier);
              return (
                <Grid size={12}>
                  <DandDButtonBox
                    name={ has_condition ? 
                      "Remove" : 
                      "Apply"
                    }
                    style={{
                      fontSize: "9px",
                    }}
                    disabled={ !enabled || processing }
                    onClick={ async () => {
                      debugger;
                      if (!has_condition) {
                        const dm = new DamageMultiplierSimple();
                        dm.damage_type = damage_type;
                        dm.multiplier = multiplier;
                        char.extra_damage_multipliers.push(dm);
                      } else {
                        char.extra_damage_multipliers = char.extra_damage_multipliers.filter(o => o.damage_type !== damage_type || o.multiplier !== multiplier);
                      }
                      char.changed();
                      char.subscription_holder.recalculate();
                    }} 
                  />
                </Grid>
              );
            }
          }
          return null;
        }
      }
    }
    
    const renderRemoveAny = () => {
      const self_condition_ids = options.map(o => o.id);
      const has_any_condition = char_util.hasCondition(char, self_condition_ids, 1);
      const has_any_dm = self_condition_ids.some(id => {
        const pieces2 = id.split(" ");
        let multiplier2 = -1;
        let multiplier_str2 = "";
        let damage_type2: DamageType = "Typeless";
        if (pieces2.length === 2) {
          const dt2 = pieces2[0];
          multiplier_str2 = pieces2[1];
          if ((multiplier_str2 === "Resistance" || 
            multiplier_str2 === "Vulnerability" || 
            multiplier_str2 === "Immunity") && isDamageType(dt2)) {
            multiplier2 = multiplier_map[multiplier_str2];
            damage_type2 = dt2;
            return char.extra_damage_multipliers.some(o => 
              o.damage_type === damage_type2 && o.multiplier === multiplier2);
          }
        }
        return false;
      });
      if (has_any_condition || has_any_dm) {
        return (
          <Grid size={12}>
            <DandDButtonBox
              name="Remove Any"
              style={{
                fontSize: "9px",
              }}
              disabled={ !enabled || processing }
              onClick={ async () => {
                const ccs = char.conditions.filter(o => self_condition_ids.includes(o.source_id));
                for (const cc of ccs) {
                  char_util.removeCharFeatureBasesFor(char, cc);
                  char.conditions = char.conditions.filter(o => o.source_id !== cc.source_id);
                }
                copyHash();
                
                for (const id of self_condition_ids) {
                  const pieces2 = id.split(" ");
                  if (pieces2.length === 2) {
                    const dt2 = pieces2[0];
                    const multiplier_str2 = pieces2[1];
                    if ((multiplier_str2 === "Resistance" || 
                      multiplier_str2 === "Vulnerability" || 
                      multiplier_str2 === "Immunity") && isDamageType(dt2)) {
                      const multiplier2 = multiplier_map[multiplier_str2];
                      const damage_type2 = dt2;
                      char.extra_damage_multipliers = char.extra_damage_multipliers.filter(o => 
                        o.damage_type !== damage_type2 || o.multiplier !== multiplier2);
                    }
                  }
                }
                char.changed();
                char.subscription_holder.recalculate();
              }} 
            />
          </Grid>
        );
      }
      return null;
    }
    
    // if (self_condition_ids.includes("69b837b5fd85afabff91defc")) {
    //   debugger;
    // }

    if (options.length === 0) {
      return null;
    } else if (options.length === 1) {
      const id = options[0].id;
      const condition = conditions.find(o => o._id === id);
      if (condition) {
        if (condition.max_level > 1) {
          let levels_applied = char_util.value(potence.condition_levels_applied, { obj: char });
          if (potence.remove_condition_levels) {
            levels_applied = -1 * levels_applied;
          }
          const current_level = condition_hash[id] ? condition_hash[id].level : 0;
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={12}>
                <DandDButtonBox
                  name={ levels_applied >= 0 ? `Apply ${levels_applied} ${condition.name}` : `Remove ${-1 * levels_applied} ${condition.name}` }
                  disabled={ !enabled || (levels_applied > 0 && current_level >= condition.max_level) || (levels_applied < 0 && current_level <= 0) || processing }
                  onClick={ async () => {
                    // debugger;
                    const cond_ids = 
                      // alternate_condition_features.length === 0 ? 
                      potence.conditions_applied; // : potence.conditions_applied.map(o => {
                    //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
                    //   if (alternate_condition_feature) {
                    //     return alternate_condition_feature.alternate_condition_id;
                    //   } else {
                    //     return o;
                    //   }
                    // });
                    for (const cond_id of cond_ids) {
                      // Apply the condition
                      const obj = data_util.conditions.find(o => o._id === cond_id);
                      if (obj) {
                        await api.loadObject(obj);
                        const cc = new CharacterCondition();
                        await cc.copy_source({ char: char, feature_source: obj });
                        cc.level = levels_applied;
                        char.conditions.push(cc);
                      }
                    }
                    copyHash();
                    char.changed();
                    char.subscription_holder.recalculate();
                  }} 
                />
              </Grid>
              { current_level > 0 && levels_applied > 0 &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name={ `Remove ${levels_applied} ${condition.name}` }
                    disabled={ !enabled || processing }
                    onClick={() => {
                      const cond_ids = 
                        potence.conditions_applied; 
                      for (const cond_id of cond_ids) {
                        const cc = char.conditions.find(o => o.source_id === cond_id);
                        if (cc) {
                          char_util.removeCharFeatureBasesFor(char, cc);
                          char.conditions = char.conditions.filter(o => o.source_id !== cc.source_id);
                        }
                      }
                      copyHash();
                      char.changed();
                      char.subscription_holder.recalculate();
                    }} 
                  />
                </Grid>
              }
              { char instanceof Character && effect.target_int.target_type !== "Self" && (char.minions.length > 0 || char.campaign) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name="Apply to Ally"
                    disabled={ !enabled || processing }
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                  { renderExtras() }
                </Grid>
              }
            </Grid>
          );
        } else {
          const label = condition_hash[id] ? 
            `Remove ${condition.name}` : 
            `Apply ${condition.name}`;
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                className="centered"
                style={{ height: "37.5" }}
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name={ label }
                  disabled={ !enabled || processing }
                  onClick={ async () => {
                    // const alternate_condition_features = char.alternate_condition_features.filter(o => potence.conditions_applied.includes(o.original_condition_id));
                    
                    const cond_ids = 
                      // alternate_condition_features.length === 0 ? 
                      potence.conditions_applied; // : potence.conditions_applied.map(o => {
                    //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
                    //   if (alternate_condition_feature) {
                    //     return alternate_condition_feature.alternate_condition_id;
                    //   } else {
                    //     return o;
                    //   }
                    // });
                    for (const cond_id of cond_ids) {
                      const cc = char.conditions.find(o => o.source_id === cond_id);
                      if (cc) {
                        char_util.removeCharFeatureBasesFor(char, cc);
                        char.conditions = char.conditions.filter(o => o.source_id !== cc.source_id);
                        // character.spells = character.spells.filter(o => o.source_type !== "Gift" || o.source_id !== char_gift.source_id);
                        // onChange();
                      } else {
                        const obj = conditions.find(o => o._id === cond_id);
                        if (obj) {
                          await api.loadObject(obj);
                          const cc = new CharacterCondition();
                          await cc.copy_source({ char: char, feature_source: obj });
                          cc.level = 1;
                          char.conditions.push(cc);
                          
                          // onChange();
                          // setPing(!ping);
                        }
                      }
                    }
                    copyHash();
                    char.changed();
                    char.subscription_holder.recalculate();
                  }} 
                />
              </Grid>
            </Grid>
          );
        }
      } else {
        const pieces = id.split(" ");
        let multiplier = -1;
        let multiplier_str = "";
        let damage_type: DamageType = "Typeless";
        let level = 0;
        if (pieces.length === 2) {
          const dt = pieces[0];
          multiplier_str = pieces[1];
          if ((multiplier_str === "Resistance" || 
            multiplier_str === "Vulnerability" || 
            multiplier_str === "Immunity") && isDamageType(dt)) {
            multiplier = multiplier_map[multiplier_str];
            damage_type = dt;
            level = char_util.value(potence.condition_levels_applied, { obj: char });
            if (potence.remove_condition_levels) {
              level = -1 * level;
            }
          }
        }
        if (multiplier === -1 || damage_type === "Typeless") {
          return <>Invalid Condition</>;
        } else {
          const current_level = char.extra_damage_multipliers.some(o => o.damage_type === damage_type && o.multiplier === multiplier) ? 1 : 0;
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={12}>
                <DandDButtonBox
                  name={ current_level === 0 ? `Apply ${damage_type} ${multiplier_str}` : `Remove ${damage_type} ${multiplier_str}` }
                  disabled={ !enabled || processing }
                  onClick={ async () => {
                    // Apply the damage multiplier
                    if (current_level === 0) {
                      const dm = new DamageMultiplierSimple();
                      dm.damage_type = damage_type;
                      dm.multiplier = multiplier;
                      char.extra_damage_multipliers.push(dm);
                    } else {
                      char.extra_damage_multipliers = char.extra_damage_multipliers.filter(o => o.damage_type !== damage_type || o.multiplier !== multiplier);
                    }
                    char.changed();
                    char.subscription_holder.recalculate();
                  }} 
                />
              </Grid>
              { char instanceof Character && effect.target_int.target_type !== "Self" && (char.minions.length > 0 || char.campaign) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name="Apply to Ally"
                    disabled={ !enabled || processing }
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                  { renderExtras() }
                </Grid>
              }
            </Grid>
          );
        }
      }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="centered"
            size={6}>
            {/* <SelectObjectBox 
              name="Condition"
              data_type="condition"
              filter={{ _id: { "in": self_condition_ids }}}
              // log_it
              value={selected_id}
              onChange={setSelectedId}
            /> */}
            <SelectStringBox
              name="Condition"
              options={options}
              // log_it
              value={selected_id}
              onChange={setSelectedId}
            />
          </Grid>
          <Grid size={6}>
            { renderApplyRemove() }
          </Grid>
        </Grid>
      );
    }
  }
}
